import React from 'react';
import { Target } from 'lucide-react';
import { C } from '../data/constants.js';
import { AnimatedNumber } from './AnimatedNumber.js';
import { EmptyState } from './EmptyState.js';

export interface ROICardProps {
  totalSaved?: number;
  wasteSavings?: number;
  deadTimeValue?: number;
  avgWastePerCase?: number;
  avgDeadTimeSavedPerCase?: number;
}

export function ROICard({
  totalSaved,
  wasteSavings,
  deadTimeValue,
  avgWastePerCase,
  avgDeadTimeSavedPerCase,
}: ROICardProps): React.ReactElement {
  if (totalSaved == null) {
    return (
      <EmptyState
        icon={Target}
        title="ROI Summary Not Available"
        message="Waste and dead time cost data is not yet being collected for this facility."
      />
    );
  }

  return (
    <div
      style={{
        background: `linear-gradient(135deg, ${C.accentDim} 0%, rgba(0,207,160,0.02) 100%)`,
        border: `1px solid ${C.accentBorder}`,
        borderRadius: 16,
        padding: '24px 28px',
        animation: 'insights-fadeSlideUp 0.6s ease 200ms both',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <Target size={18} color={C.accent} />
        <span style={{ fontSize: 14, fontWeight: 600, color: C.accent }}>ROI Summary</span>
      </div>
      <div
        style={{
          fontSize: 38,
          fontWeight: 700,
          color: '#fff',
          fontFamily: "'Space Mono', monospace",
          letterSpacing: -2,
        }}
      >
        <AnimatedNumber value={totalSaved} prefix="$" />
      </div>
      <div style={{ fontSize: 12, color: C.textDim, marginTop: 4 }}>
        Total value identified (waste + dead time opportunity)
      </div>
      <div style={{ display: 'flex', gap: 24, marginTop: 16, flexWrap: 'wrap' }}>
        {wasteSavings != null && (
          <div>
            <div style={{ fontSize: 11, color: C.textFaint }}>Waste Savings</div>
            <div
              style={{
                fontSize: 16,
                fontWeight: 600,
                color: C.orange,
                fontFamily: "'Space Mono', monospace",
              }}
            >
              ${wasteSavings.toLocaleString()}
            </div>
          </div>
        )}
        {deadTimeValue != null && (
          <div>
            <div style={{ fontSize: 11, color: C.textFaint }}>Dead Time Value</div>
            <div
              style={{
                fontSize: 16,
                fontWeight: 600,
                color: C.purple,
                fontFamily: "'Space Mono', monospace",
              }}
            >
              ${deadTimeValue.toLocaleString()}
            </div>
          </div>
        )}
        {avgWastePerCase != null && (
          <div>
            <div style={{ fontSize: 11, color: C.textFaint }}>Avg Waste per Case</div>
            <div
              style={{
                fontSize: 16,
                fontWeight: 600,
                color: C.accent,
                fontFamily: "'Space Mono', monospace",
              }}
            >
              ${avgWastePerCase.toFixed(2)}
            </div>
          </div>
        )}
        {avgDeadTimeSavedPerCase != null && (
          <div>
            <div style={{ fontSize: 11, color: C.textFaint }}>Avg Dead Time Saved / Case</div>
            <div
              style={{
                fontSize: 16,
                fontWeight: 600,
                color: C.accent,
                fontFamily: "'Space Mono', monospace",
              }}
            >
              {avgDeadTimeSavedPerCase.toFixed(1)} min
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
